// RigSentinel Context Engine — Phase RCE-1
//
// OVERRIDES
// ---------
// Two override scopes:
//   * document — applies only to a specific document (e.g. one kill sheet).
//                Overrides never bleed into other documents or the master
//                record.
//   * master   — applies to a master record (well, rig, project, BHA,
//                drill string). Used when the user explicitly chooses to
//                edit the master through the override flow rather than the
//                module's own editor. Phase RCE-1 only stores them; later
//                phases will surface a UI to push them back into the
//                respective master records.
//
// Storage key: `rsc.overrides.v1`.
//
// Override record shape:
//   {
//     id,
//     scope:    "document" | "master",
//     scopeId:  string (documentId / wellId / rigId / projectId / etc.),
//     fieldPath: "rce-known field path (see classification.js)",
//     value,
//     reason: "",
//     createdAt, updatedAt,
//   }

import { emit, RCE_EVENTS } from "./events";
import { isInheritable, classifyField } from "./classification";
import { scoped } from "@/lib/accountScope";

export const OVERRIDES_STORAGE_KEY = "rsc.overrides.v1";

const OVERRIDE_SCOPES = ["document", "master"];

const _now = () => new Date().toISOString();
const _genId = () =>
  `ovr_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
const _safeParse = (s, f) => {
  try { return JSON.parse(s) ?? f; } catch { return f; }
};

function _readAll() {
  return _safeParse(scoped.getItem(OVERRIDES_STORAGE_KEY), []);
}
function _writeAll(rows) {
  scoped.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(rows));
}

/**
 * Set (or replace) an override.
 *
 * `value === ""` or `value === null` is treated as "user wants engine to
 * resolve freely again" and clears the override.
 *
 * Document-only fields are NOT eligible for override — they belong to a
 * specific document and have no inheritance to override.
 */
export function setOverride({ scope, scopeId, fieldPath, value, reason = "" }) {
  if (!OVERRIDE_SCOPES.includes(scope))
    throw new Error(`Override scope must be one of: ${OVERRIDE_SCOPES.join(", ")}.`);
  if (!scopeId) throw new Error("Override scopeId is required.");
  if (!fieldPath) throw new Error("Override fieldPath is required.");

  // Refuse overrides on document-only fields — those carry no inheritance.
  const kind = classifyField(fieldPath);
  if (kind === "document-only")
    throw new Error(`Field "${fieldPath}" is document-only and cannot be overridden.`);
  // Allow overrides on unknown fields — the registry will grow over time and
  // we don't want to refuse a user fix because the field hasn't been
  // registered yet.

  // Clearing semantics.
  if (value === "" || value === null || value === undefined) {
    const removed = clearOverride({ scope, scopeId, fieldPath });
    return removed;
  }

  const all = _readAll();
  const existing = all.find((r) =>
    r.scope === scope && r.scopeId === scopeId && r.fieldPath === fieldPath);
  if (existing) {
    existing.value = value;
    if (reason !== undefined) existing.reason = String(reason || "");
    existing.updatedAt = _now();
    _writeAll(all);
    try { emit(RCE_EVENTS.OVERRIDE_SET, { id: existing.id, scope, scopeId, fieldPath }); } catch { /* noop */ }
    return existing;
  }
  const row = {
    id: _genId(),
    scope, scopeId, fieldPath,
    value,
    reason: String(reason || ""),
    createdAt: _now(),
    updatedAt: _now(),
  };
  all.push(row);
  _writeAll(all);
  try { emit(RCE_EVENTS.OVERRIDE_SET, { id: row.id, scope, scopeId, fieldPath }); } catch { /* noop */ }
  return row;
}

/** Clear an override (no-op if none exists). Returns the removed row or null. */
export function clearOverride({ scope, scopeId, fieldPath }) {
  const all = _readAll();
  const i = all.findIndex((r) =>
    r.scope === scope && r.scopeId === scopeId && r.fieldPath === fieldPath);
  if (i < 0) return null;
  const removed = all[i];
  all.splice(i, 1);
  _writeAll(all);
  try { emit(RCE_EVENTS.OVERRIDE_CLEARED, { scope, scopeId, fieldPath }); } catch { /* noop */ }
  return removed;
}

/** Return an override row (or null) matching scope+scopeId+fieldPath. */
export function getOverride({ scope, scopeId, fieldPath }) {
  return _readAll().find((r) =>
    r.scope === scope && r.scopeId === scopeId && r.fieldPath === fieldPath) || null;
}

/**
 * Resolve the active override for a field path given a context. Document
 * overrides win over master overrides (the user is editing the document and
 * has the final word for that document).
 *
 * `ctx`: { documentId?, wellId?, rigId?, projectId?, bhaId?, drillStringId? }
 *
 * Document-only fields are not eligible (returns null). Non-inheritable
 * fields (unknown kind) still allow overrides — see setOverride().
 */
export function resolveOverride(fieldPath, ctx = {}) {
  if (!fieldPath) return null;
  const k = classifyField(fieldPath);
  if (k === "document-only") return null;

  const all = _readAll();
  const candidates = [];

  if (ctx.documentId) {
    const hit = all.find((r) =>
      r.scope === "document" && r.scopeId === ctx.documentId && r.fieldPath === fieldPath);
    if (hit) candidates.push({ row: hit, weight: 1 });
  }
  const masterIds = [
    ctx.wellId, ctx.rigId, ctx.projectId, ctx.bhaId, ctx.drillStringId,
  ].filter(Boolean);
  for (const mid of masterIds) {
    const hit = all.find((r) =>
      r.scope === "master" && r.scopeId === mid && r.fieldPath === fieldPath);
    if (hit) candidates.push({ row: hit, weight: 2 });
  }
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.weight - b.weight);
  return candidates[0].row;
}

/** List every override matching a scope (or scopeId). Convenience for UI. */
export function listOverrides({ scope, scopeId } = {}) {
  let rows = _readAll();
  if (scope) rows = rows.filter((r) => r.scope === scope);
  if (scopeId) rows = rows.filter((r) => r.scopeId === scopeId);
  return rows;
}

/** Test helper — never called from app code. */
export function _clearAllOverrides() {
  scoped.removeItem(OVERRIDES_STORAGE_KEY);
}

export { OVERRIDE_SCOPES, isInheritable };
